import Header from '../../../components/feature/Header';
import Footer from '../../../components/feature/Footer';
import { Link } from 'react-router-dom';
import { useEffect } from 'react';

const Bismarck = () => {
  useEffect(() => {
    // Set document title and meta tags
    document.title = 'We Buy Valves Bismarck | Industrial Equipment Buyers Bismarck North Dakota';
    
    // Set meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Professional valve buyers in Bismarck serving North Dakota capital region. We buy surplus valves, control systems, and instrumentation from Bismarck industrial facilities at above scrap rates.');
    }
    
    // Set meta keywords
    let metaKeywords = document.querySelector('meta[name="keywords"]');
    if (!metaKeywords) {
      metaKeywords = document.createElement('meta');
      metaKeywords.setAttribute('name', 'keywords');
      document.head.appendChild(metaKeywords);
    }
    metaKeywords.setAttribute('content', 'we buy valves bismarck, bismarck valve buyers, north dakota industrial equipment buyers, bismarck surplus valves');
    
    // Set canonical URL
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', 'https://valvesalessupply.com/bismarck');
    
    // Set Open Graph tags
    const ogTags = [
      { property: 'og:title', content: 'We Buy Valves Bismarck | Industrial Equipment Buyers Bismarck North Dakota' },
      { property: 'og:description', content: 'Professional valve buyers in Bismarck serving North Dakota capital region industrial facilities.' },
      { property: 'og:url', content: 'https://valvesalessupply.com/bismarck' },
      { property: 'og:type', content: 'website' }
    ];
    
    ogTags.forEach(tag => {
      let ogTag = document.querySelector(`meta[property="${tag.property}"]`);
      if (!ogTag) {
        ogTag = document.createElement('meta');
        ogTag.setAttribute('property', tag.property);
        document.head.appendChild(ogTag);
      }
      ogTag.setAttribute('content', tag.content);
    });
    
    // Set geo location tags
    let geoPosition = document.querySelector('meta[name="geo.position"]');
    if (!geoPosition) {
      geoPosition = document.createElement('meta');
      geoPosition.setAttribute('name', 'geo.position');
      document.head.appendChild(geoPosition);
    }
    geoPosition.setAttribute('content', '46.8083;-100.7837');
    
    let geoRegion = document.querySelector('meta[name="geo.region"]');
    if (!geoRegion) {
      geoRegion = document.createElement('meta');
      geoRegion.setAttribute('name', 'geo.region');
      document.head.appendChild(geoRegion);
    }
    geoRegion.setAttribute('content', 'US-ND');
    
    let geoPlacename = document.querySelector('meta[name="geo.placename"]');
    if (!geoPlacename) {
      geoPlacename = document.createElement('meta');
      geoPlacename.setAttribute('name', 'geo.placename');
      document.head.appendChild(geoPlacename);
    }
    geoPlacename.setAttribute('content', 'Bismarck, North Dakota');

    // Add Schema.org structured data
    const schemaData = {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "LocalBusiness",
          "name": "ValveSalesSupply Bismarck",
          "address": {
            "@type": "PostalAddress",
            "addressLocality": "Bismarck",
            "addressRegion": "ND",
            "addressCountry": "US"
          },
          "geo": {
            "@type": "GeoCoordinates",
            "latitude": 46.8083,
            "longitude": -100.7837
          },
          "telephone": "+1-214-854-5638",
          "description": "Professional valve buyers in Bismarck serving North Dakota capital region. We purchase surplus valves, control systems, and instrumentation from industrial facilities."
        },
        {
          "@type": "BreadcrumbList",
          "itemListElement": [
            {
              "@type": "ListItem",
              "position": 1,
              "name": "Home",
              "item": "https://valvesalessupply.com"
            },
            {
              "@type": "ListItem",
              "position": 2,
              "name": "North Dakota",
              "item": "https://valvesalessupply.com/north-dakota"
            },
            {
              "@type": "ListItem",
              "position": 3,
              "name": "Bismarck",
              "item": "https://valvesalessupply.com/bismarck"
            }
          ]
        },
        {
          "@type": "FAQPage",
          "mainEntity": [
            {
              "@type": "Question",
              "name": "Do you buy valves in Bismarck?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Yes, we buy surplus valves in Bismarck including control valves, gate valves, ball valves, and instrumentation from industrial facilities at above scrap rates with 8-10 hour response times."
              }
            },
            {
              "@type": "Question",
              "name": "What equipment do you purchase in Bismarck?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "We purchase Fisher control valves, Masoneilan valves, Rosemount transmitters, flow meters, pressure instruments, wellhead equipment, and complete instrumentation systems from Bismarck facilities."
              }
            }
          ]
        }
      ]
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(schemaData);
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, []);

  const nearbyFacilities = [
    { name: 'Oil & Gas Operations', type: 'Production', capacity: '1,000+ Wells' },
    { name: 'Manufacturing Plants', type: 'Industrial', capacity: '50+ Facilities' },
    { name: 'Processing Plants', type: 'Gas Processing', capacity: 'Regional Facilities' }
  ];

  return (
    <div className="min-h-screen">
      <Header />
      
      {/* Hero Section */}
      <section className="relative pt-24 pb-16">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url('https://public.readdy.ai/ai/img_res/07033bd3f04acc8e9ae3144454648c74.jpg')`
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-[#1B3A5F]/90 to-[#1B3A5F]/70"></div>
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 text-white">
          <div className="max-w-4xl">
            <h1 className="text-5xl font-bold mb-6">We Buy Valves Bismarck</h1>
            <p className="text-xl text-gray-200 mb-8 leading-relaxed">
              Professional <strong>valve buyers in Bismarck</strong> serving North Dakota's capital region industrial facilities. 
              Valve Sales Supply purchases surplus valves, control systems, and instrumentation from Bismarck oil and gas 
              operations at above scrap rates.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <a
                href="tel:214-854-5638"
                className="bg-white text-[#1B3A5F] px-8 py-4 rounded-full font-bold text-lg hover:bg-gray-100 transition-all duration-300 inline-flex items-center justify-center whitespace-nowrap"
              >
                <i className="ri-phone-line mr-2"></i>
                CALL 214-854-5638
              </a>
              <Link
                to="/contact"
                className="border-2 border-white text-white px-8 py-4 rounded-full font-bold text-lg hover:bg-white hover:text-[#1B3A5F] transition-all duration-300 inline-flex items-center justify-center whitespace-nowrap"
              >
                GET BISMARCK QUOTE
                <i className="ri-arrow-right-line ml-2"></i>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Market Overview */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-[#1B3A5F] text-4xl font-bold mb-6">BISMARCK INDUSTRIAL EQUIPMENT BUYER</h2>
              <p className="text-gray-600 text-lg mb-6 leading-relaxed">
                Bismarck serves as North Dakota's capital with growing oil and gas operations and manufacturing. 
                As experienced <strong className="text-[#1B3A5F]">surplus valve buyers in Bismarck</strong>, 
                we provide professional equipment purchasing services for all industrial sectors.
              </p>
              <p className="text-gray-600 text-lg mb-8 leading-relaxed">
                We specialize in purchasing <strong className="text-[#1B3A5F]">Fisher control valves</strong>, 
                Rosemount transmitters, and complete instrumentation systems from Bismarck industrial facilities.
              </p>
              
              <div className="grid md:grid-cols-3 gap-6">
                <div className="text-center">
                  <div className="text-[#1B3A5F] text-3xl font-bold">1,000+</div>
                  <div className="text-gray-600">Production Wells</div>
                </div>
                <div className="text-center">
                  <div className="text-[#1B3A5F] text-3xl font-bold">8-10 Hrs</div>
                  <div className="text-gray-600">Response Time</div>
                </div>
                <div className="text-center">
                  <div className="text-[#1B3A5F] text-3xl font-bold">15 Years</div>
                  <div className="text-gray-600">ND Experience</div>
                </div>
              </div>
            </div>
            
            <div className="bg-[#F5F7FA] rounded-3xl p-8">
              <h3 className="text-[#1B3A5F] text-2xl font-bold mb-6">BISMARCK FACILITIES</h3>
              <div className="space-y-4">
                {nearbyFacilities.map((facility, index) => (
                  <div key={index} className="bg-white rounded-lg p-4">
                    <h4 className="text-[#1B3A5F] font-bold text-lg">{facility.name}</h4>
                    <div className="flex justify-between mt-2">
                      <span className="text-gray-600 text-sm">{facility.type}</span>
                      <span className="text-[#1B3A5F] font-semibold text-sm">{facility.capacity}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Valve Buying Process */}
      <section className="py-20 bg-[#F5F7FA]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-[#1B3A5F] text-4xl font-bold mb-4">VALVE BUYING PROCESS IN BISMARCK</h2>
            <p className="text-gray-600 text-xl">Simple 4-step process to sell your surplus valves</p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                step: '1',
                title: 'Contact Us',
                description: 'Call or email with your equipment list and photos',
                icon: 'ri-phone-line'
              },
              {
                step: '2',
                title: 'Get Quote',
                description: 'Receive competitive above-scrap pricing within hours',
                icon: 'ri-file-list-3-line'
              },
              {
                step: '3',
                title: 'Schedule Pickup',
                description: 'We coordinate removal at your convenience',
                icon: 'ri-calendar-check-line'
              },
              {
                step: '4',
                title: 'Get Paid',
                description: 'Fast payment upon equipment pickup',
                icon: 'ri-money-dollar-circle-line'
              }
            ].map((process, index) => (
              <div key={index} className="bg-white rounded-3xl p-8 text-center hover:shadow-lg transition-all duration-300">
                <div className="w-16 h-16 bg-[#1B3A5F] rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className={`${process.icon} text-white text-2xl`}></i>
                </div>
                <div className="text-[#1B3A5F] text-4xl font-bold mb-2">{process.step}</div>
                <h3 className="text-[#1B3A5F] text-xl font-bold mb-3">{process.title}</h3>
                <p className="text-gray-600">{process.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Equipment Categories */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-[#1B3A5F] text-4xl font-bold mb-4">SELL VALVES BISMARCK ND</h2>
            <p className="text-gray-600 text-xl">Professional equipment purchasing from Bismarck industrial facilities</p>
          </div>
          
          <div className="grid lg:grid-cols-3 gap-8">
            {[
              {
                title: 'Control Valves',
                equipment: ['Fisher Control Valves', 'Masoneilan Valves', 'Ball Valves', 'Gate Valves', 'Globe Valves'],
                description: 'Premium control valves from Bismarck facilities'
              },
              {
                title: 'Production Equipment',
                equipment: ['Wellhead Equipment', 'Separators', 'Pumps', 'Safety Systems'],
                description: 'Oil & gas equipment from North Dakota operations'
              },
              {
                title: 'Instrumentation',
                equipment: ['Rosemount Transmitters', 'Flow Meters', 'Pressure Instruments', 'Controllers'],
                description: 'Process instrumentation from ND facilities'
              }
            ].map((category, index) => (
              <div key={index} className="bg-[#F5F7FA] rounded-3xl p-8 hover:shadow-lg transition-all duration-300">
                <h3 className="text-[#1B3A5F] text-2xl font-bold mb-4">{category.title}</h3>
                <p className="text-gray-600 mb-6">{category.description}</p>
                
                <div className="space-y-3">
                  {category.equipment.map((item, itemIndex) => (
                    <div key={itemIndex} className="flex items-center">
                      <i className="ri-check-line text-[#1B3A5F] mr-3"></i>
                      <span className="text-gray-700">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Related Markets */}
      <section className="py-20 bg-[#F5F7FA]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-[#1B3A5F] text-4xl font-bold mb-4">RELATED NORTH DAKOTA MARKETS</h2>
            <p className="text-gray-600 text-xl">We serve all major North Dakota industrial centers</p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { city: 'Williston', link: '/williston' },
              { city: 'Minot', link: '/minot' },
              { city: 'Dickinson', link: '/dickinson' }
            ].map((market, index) => (
              <Link
                key={index}
                to={market.link}
                className="bg-white rounded-xl p-6 text-center hover:shadow-lg transition-all duration-300 border-2 border-transparent hover:border-[#1B3A5F]"
              >
                <h3 className="text-[#1B3A5F] text-xl font-bold mb-2">{market.city}</h3>
                <div className="text-gray-600 text-sm mb-3">ND Valve Buyers</div>
                <div className="text-[#1B3A5F] font-semibold inline-flex items-center">
                  View Details
                  <i className="ri-arrow-right-line ml-2"></i>
                </div>
              </Link>
            ))}
          </div>
          
          <div className="text-center mt-8">
            <Link
              to="/north-dakota"
              className="text-[#1B3A5F] font-semibold hover:underline inline-flex items-center text-lg"
            >
              View All North Dakota Cities
              <i className="ri-arrow-right-line ml-2"></i>
            </Link>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-20 bg-[#1B3A5F]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="text-white">
              <h2 className="text-4xl font-bold mb-6">BISMARCK EQUIPMENT BUYER</h2>
              <p className="text-xl text-gray-200 mb-8">
                Contact Mark Driver for professional equipment purchasing services in Bismarck
              </p>
              
              <div className="space-y-4">
                <div className="flex items-center">
                  <i className="ri-phone-line text-2xl mr-4"></i>
                  <div>
                    <div className="font-bold text-xl">214-854-5638</div>
                    <div className="text-gray-300">Direct Line - Mark Driver</div>
                  </div>
                </div>
                
                <div className="flex items-center">
                  <i className="ri-mail-line text-2xl mr-4"></i>
                  <div>
                    <div className="font-bold">bismarck@valvesalessupply.com</div>
                    <div className="text-gray-300">Equipment Lists & Photos</div>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="bg-white rounded-3xl p-8">
              <h3 className="text-[#1B3A5F] text-2xl font-bold mb-6">WHY CHOOSE VALVE SALES SUPPLY</h3>
              <div className="space-y-4">
                {[
                  'Above scrap rates for functional equipment',
                  'Fast 8-10 hour response in Bismarck',
                  'Expert knowledge of industrial equipment',
                  'Professional turnaround coordination',
                  'Complete compliance documentation',
                  '15+ years North Dakota market experience'
                ].map((benefit, index) => (
                  <div key={index} className="flex items-center">
                    <i className="ri-check-line text-[#1B3A5F] text-xl mr-3"></i>
                    <span className="text-gray-700">{benefit}</span>
                  </div>
                ))}
              </div>
              
              <div className="mt-8">
                <Link
                  to="/contact"
                  className="bg-[#1B3A5F] text-white px-8 py-4 rounded-full font-bold hover:bg-[#1B3A5F]/90 transition-all duration-300 inline-flex items-center justify-center w-full whitespace-nowrap"
                >
                  GET QUOTE TODAY
                  <i className="ri-arrow-right-line ml-2"></i>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Bismarck;
